'use client';

import { useClickOutside } from '@/hooks/use-click-outside';
import clsx from 'clsx';
import { FC, PropsWithChildren, ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import { cross } from '@/assets';
import { overlayLayers } from '@/ui/overlay-layers';
import { OverlayPortalContext } from '@/ui/overlay-portal-context';

export type ModalProps = PropsWithChildren & {
    isShown: boolean;
    setIsShown: (val: boolean) => void;
    title?: string;
    headerContent?: ReactNode;
    headerContentPlacement?: 'center' | 'left';
    titlePrefix?: ReactNode;
    footer?: ReactNode | string;
    closable?: boolean;
    className?: string;
    hideHeader?: boolean;
};

const isTopMostModal = (element: HTMLDivElement | null): boolean => {
    if (!element) {
        return false;
    }

    const modalRoots = Array.from(document.querySelectorAll<HTMLDivElement>('[data-modal-root="true"]'));
    if (!modalRoots.length) {
        return false;
    }

    return modalRoots[modalRoots.length - 1] === element;
};

const Modal: FC<ModalProps> = ({
    isShown,
    setIsShown,
    title,
    headerContent,
    headerContentPlacement = 'center',
    titlePrefix,
    children,
    closable,
    footer,
    className,
    hideHeader,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
    const [overlayPortalTarget, setOverlayPortalTarget] = useState<HTMLElement | null>(null);

    const handleOverlaySlotRef = useCallback((element: HTMLDivElement | null) => {
        setOverlayPortalTarget(element);
    }, []);

    useEffect(() => {
        setPortalTarget(document.body);
    }, []);

    useEffect(() => {
        if (!isShown) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isTopMostModal(containerRef.current)) {
                setIsShown(false);
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isShown, setIsShown]);

    useClickOutside(containerRef, () => {
        if (!isTopMostModal(containerRef.current)) {
            return;
        }

        setIsShown(false);
    });

    const overlayPortalContextValue = useMemo(
        () => ({
            portalTarget: overlayPortalTarget,
            isInsideModal: true,
        }),
        [overlayPortalTarget],
    );

    if (!portalTarget || !isShown) {
        return null;
    }

    const resolvedTitle = title || 'Заголовок';
    const dialogLabel = hideHeader ? resolvedTitle : undefined;
    const hasHeaderContent = headerContent !== undefined;

    return createPortal(
        <OverlayPortalContext.Provider value={overlayPortalContextValue}>
            <div
                className={clsx(
                    'w-screen h-screen fixed top-0 left-0 visible bg-black/75',
                    'flex justify-center items-center',
                )}
                style={{ zIndex: overlayLayers.overlayBackdrop }}
            >
                <div
                    ref={handleOverlaySlotRef}
                    className="pointer-events-none absolute inset-0"
                    style={{ zIndex: overlayLayers.overlayModalPopover }}
                />
                <div
                    ref={containerRef}
                    className={clsx('relative w-140 h-10/12 bg-white rounded-lg', className)}
                    style={{ zIndex: overlayLayers.overlayModalContent }}
                    data-modal-root="true"
                    data-click-outside-ignore="true"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={!hideHeader ? titleId : undefined}
                    aria-label={dialogLabel}
                >
                    <div className="flex h-full flex-col">
                        {!hideHeader && (
                            <header className="relative grid w-full grid-cols-[1fr_auto] items-start gap-3 px-4 pt-4 pb-2 text-sm leading-5">
                                <div className="flex min-h-7 min-w-0 items-center justify-start">
                                    {titlePrefix ? <div className="-mt-1">{titlePrefix}</div> : null}
                                </div>
                                <div
                                    data-modal-header-content="true"
                                    className={clsx(
                                        'absolute top-4 flex min-h-7 items-center',
                                        headerContentPlacement === 'left'
                                            ? 'left-4 right-12 justify-start'
                                            : 'inset-x-0 justify-center px-12',
                                        hasHeaderContent ? 'pointer-events-auto' : 'pointer-events-none',
                                    )}
                                >
                                    <div id={titleId} className="min-w-0">
                                        {headerContent ?? resolvedTitle}
                                    </div>
                                </div>
                                <div className="flex min-h-7 items-center justify-end">
                                    {closable && (
                                        <button
                                            type="button"
                                            aria-label="Скрыть диалог"
                                            className="justify-self-end cursor-pointer"
                                            onClick={() => setIsShown(false)}
                                        >
                                            <Image src={cross} alt="cross" width={20} height={20} />
                                        </button>
                                    )}
                                </div>
                            </header>
                        )}
                        <div
                            className={clsx(
                                'flex h-full grow shrink-0 min-h-0',
                                hideHeader ? 'max-h-full' : 'max-h-[calc(100%-52px)]',
                            )}
                        >
                            {children}
                        </div>
                        {footer && <footer className="w-full flex justify-center items-center">{footer}</footer>}
                    </div>
                </div>
            </div>
        </OverlayPortalContext.Provider>,
        portalTarget,
    );
};

export default Modal;
